/**
 * Client-side orchestrator for the 4-stage Arpentia analysis pipeline.
 *
 * Each stage maps 1:1 to a callable Cloud Function. We call them in sequence
 * (rather than in parallel or via a monolith callable) so the Nouvelle page's
 * Generation panel can surface real progress — each stage emits a status event
 * and updates the UI independently.
 *
 * Usage:
 *   const result = await runPipeline({
 *     address: "12 rue des Lilas, 92100 Boulogne",
 *     onProgress: (stage, status) => setStageStatus(stage, status),
 *   });
 *
 * Errors are thrown as instances of PipelineError with a stable .code so the
 * UI can branch on specific failures (e.g., render a "cette commune n'a pas de
 * PLU numérique" state for `zone-not-found`).
 */

import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase";
import type {
  GeocodeResult,
  CadastreParcel,
  PluZone,
  Reglement,
  PipelineErrorCode,
  PluUploadValidation,
} from "./types";

// ─── Stage definitions ───────────────────────────────────────────────────
export type StageKey =
  | "geocode"
  | "cadastre"
  | "footprint"
  | "zone"
  | "reglement";
export type StageStatus = "pending" | "running" | "done" | "error";
export type StageEvent = {
  stage: StageKey;
  status: StageStatus;
  message?: string;
  /** Set when the call succeeded but the result is suspicious enough that
   *  the UI should flag it (e.g. BD TOPO returned 0 buildings — legitimate
   *  for terrain nu, but more often a stale dataset). The pipeline still
   *  proceeds; the UI just renders an amber-attention dot instead of the
   *  default green check. */
  attention?: boolean;
};

// Subset of `BuildingFootprint` exposed to the client. Only what the form
// pre-fill and the Hero display care about — keeps the cross-boundary type
// minimal.
export type FootprintSummary = {
  parcelArea_m2: number;
  builtFootprint_m2: number;
  buildingCount: number;
  maxHeight_m: number | null;
};

// ─── Error type ──────────────────────────────────────────────────────────
export class PipelineError extends Error {
  readonly code: PipelineErrorCode | "unknown";
  readonly stage: StageKey;
  readonly details?: unknown;
  constructor(opts: {
    code: PipelineErrorCode | "unknown";
    stage: StageKey;
    message: string;
    details?: unknown;
  }) {
    super(opts.message);
    this.code = opts.code;
    this.stage = opts.stage;
    this.details = opts.details;
  }
}

// ─── Callable bindings ───────────────────────────────────────────────────
const callGeocode = httpsCallable<{ address: string }, GeocodeResult>(
  functions,
  "geocodeAddress",
);
const callCadastre = httpsCallable<
  {
    lat: number;
    lng: number;
    ref?: {
      section: string | null;
      numero: string | null;
      prefixe: string | null;
      insee: string | null;
    } | null;
  },
  { parcel: CadastreParcel; alternatives: CadastreParcel[] }
>(functions, "getCadastreParcel");
const callZone = httpsCallable<
  { lat: number; lng: number; insee: string },
  PluZone
>(functions, "getPluZone");
const callFootprint = httpsCallable<
  { parcelGeom: unknown; parcelArea_m2?: number | null },
  FootprintSummary & { buildings: unknown[] }
>(functions, "getBuildingFootprint");
const callReglement = httpsCallable<
  {
    insee: string;
    zoneCode: string;
    communeName: string;
    reglementUrl: string;
    partition?: string | null;
    nomfic?: string | null;
    reglementPdfUrlOverride?: string;
    userUpload?: {
      ficheId: string;
      reglementStorageObject: string;
    };
  },
  Reglement
>(functions, "extractReglement", {
  // Cold start + ZIP download (up to ~600 MB) + Claude extraction on a large
  // PDF can comfortably exceed the 70s httpsCallable default. The server-side
  // timeout is 540s; match it here so the client waits.
  timeout: 540_000,
});

// PLU upload fallback — validates a user-uploaded règlement + plan graphique
// and tries to predict the applicable zone. The user always confirms in UI
// (the Vision result only pre-fills the select).
const callValidatePluUpload = httpsCallable<
  {
    ficheId: string;
    expectedInsee: string;
    expectedCommuneName: string;
    lat: number;
    lng: number;
    parcelGeometryJson: string;
  },
  PluUploadValidation
>(functions, "validatePluUpload", {
  timeout: 180_000, // matches server-side timeoutSeconds
});

/**
 * Run only the règlement-extraction stage from a user-uploaded PLU. Used
 * after the user uploaded both PDFs, validated them, and confirmed the zone
 * in the modal — at that point we already have geocode/cadastre/footprint
 * from the previous pipeline pass and only need stage 5 to complete.
 */
export async function extractReglementFromUserUpload(args: {
  uid: string;
  ficheId: string;
  insee: string;
  communeName: string;
  zoneCode: string;
}): Promise<Reglement> {
  const reglementStorageObject = `users/${args.uid}/fiches/${args.ficheId}/pluUploads/reglement.pdf`;
  const res = await callReglement({
    insee: args.insee,
    zoneCode: args.zoneCode,
    communeName: args.communeName,
    // The audit URL still has to be a string; storage:// scheme makes the
    // origin explicit when this fiche is exported or debugged later.
    reglementUrl: `storage://${reglementStorageObject}`,
    partition: null,
    nomfic: null,
    userUpload: {
      ficheId: args.ficheId,
      reglementStorageObject,
    },
  });
  return res.data;
}

/**
 * Re-runs the BD TOPO footprint stage for a given parcel — used when the
 * user picks a different parcel via the picker after the pipeline already
 * completed. The original pipeline computed footprint on the auto-picked
 * parcel; this lets the UI refresh the value when the user overrides.
 *
 * Returns null on any error — BD TOPO is non-fatal in the pipeline so we
 * mirror that here.
 */
export async function refetchFootprint(
  parcel: CadastreParcel,
): Promise<FootprintSummary | null> {
  try {
    const res = await callFootprint({
      parcelGeom: parcel.geometry,
      parcelArea_m2: parcel.surface_m2,
    });
    return {
      parcelArea_m2: res.data.parcelArea_m2,
      builtFootprint_m2: res.data.builtFootprint_m2,
      buildingCount: res.data.buildingCount,
      maxHeight_m: res.data.maxHeight_m,
    };
  } catch {
    return null;
  }
}

export async function validatePluUpload(args: {
  ficheId: string;
  expectedInsee: string;
  expectedCommuneName: string;
  lat: number;
  lng: number;
  parcelGeometryJson: string;
}): Promise<PluUploadValidation> {
  const res = await callValidatePluUpload(args);
  return res.data;
}

// ─── PLU pin refinement (click-to-pin in modal) ─────────────────────────
export type RefinedPluZone = {
  suggestedZone: string | null;
  confidence: "high" | "medium" | "low" | null;
  reasoning: string;
};

const callRefinePluZoneFromPin = httpsCallable<
  {
    ficheId: string;
    pageImageBase64: string;
    knownZones: string[];
  },
  RefinedPluZone
>(functions, "refinePluZoneFromPin", {
  timeout: 60_000,
});

export async function refinePluZoneFromPin(args: {
  ficheId: string;
  pageImageBase64: string;
  knownZones: string[];
}): Promise<RefinedPluZone> {
  const res = await callRefinePluZoneFromPin(args);
  return res.data;
}

// ─── Public API ──────────────────────────────────────────────────────────
export type PipelineInput = {
  address: string;
  onProgress?: (e: StageEvent) => void;
  // Cumulative partial result emitted after each stage that succeeds — lets
  // the caller render incremental progress AND have access to early-stage
  // data when a later stage fails. Used by the manual-PLU-upload fallback
  // (we need geocode+parcel to power the upload modal even though zone &
  // reglement never made it).
  onPartialResult?: (partial: Partial<PipelineResult>) => void;
  // Optional cadastre reference — usually sourced from a PDF extraction (compromis,
  // mandat). When provided, the cadastre stage looks up by ref and falls back to
  // coords only if the ref returns nothing. This bypasses geocoding imprecision
  // on communes with large mixed parcels.
  cadastreRef?: {
    section: string | null;
    numero: string | null;
    prefixe: string | null;
  } | null;
  /** Invoked mid-pipeline when the cadastre stage returned multiple
   *  candidate parcels — pipeline pauses on the returned promise. The
   *  caller renders a picker UI and resolves with the chosen parcel
   *  (single or merged MULTI). Reject with `PipelineError({ code:
   *  "cancelled-by-user" })` to abort cleanly. When omitted, the auto-
   *  pick heuristic from the server takes over silently (legacy
   *  behaviour). */
  onParcelChoice?: (args: {
    candidates: CadastreParcel[];
    autoPick: CadastreParcel;
  }) => Promise<CadastreParcel>;
  // Optional geocode shortcut — supplied when the user picked a BAN
  // autocomplete suggestion in step 1. We trust the BAN response we already
  // have in hand and skip the geocodeAddress Cloud Function call, saving one
  // round-trip (~400 ms) and one callable quota unit per run.
  prefetchedGeocode?: GeocodeResult | null;
};

export type PipelineResult = {
  geocode: GeocodeResult;
  parcel: CadastreParcel;
  // Other parcels within the coords-lookup buffer. Populated when the cadastre
  // stage resolved by coords and found multiple candidates — the UI should
  // expose a picker so the user can switch parcels if our auto-pick was wrong.
  // Empty when resolved by ref, or when a single parcel was found.
  parcelAlternatives: CadastreParcel[];
  // Empreinte bâtie IGN BD TOPO, clippée à la parcelle. null quand le WFS a
  // échoué (stage non-fatal) : on laisse l'agent saisir manuellement.
  footprint: FootprintSummary | null;
  zone: PluZone;
  reglement: Reglement;
};

export async function runPipeline(input: PipelineInput): Promise<PipelineResult> {
  const emit = (
    stage: StageKey,
    status: StageStatus,
    message?: string,
    extra?: { attention?: boolean },
  ) =>
    input.onProgress?.({
      stage,
      status,
      message,
      attention: extra?.attention,
    });
  const partial: Partial<PipelineResult> = {};
  const emitPartial = () => input.onPartialResult?.({ ...partial });

  // ─── Stage 1: geocode ─────────────────────────────────────────────
  emit("geocode", "running");
  const geocode = input.prefetchedGeocode
    ? input.prefetchedGeocode
    : await runStage("geocode", async () => {
        const res = await callGeocode({ address: input.address });
        return res.data;
      });
  emit(
    "geocode",
    "done",
    input.prefetchedGeocode
      ? `${geocode.label} (pré-rempli)`
      : geocode.label,
  );
  partial.geocode = geocode;
  emitPartial();

  // ─── Stage 2: cadastre ────────────────────────────────────────────
  emit("cadastre", "running");
  const refComplete =
    !!input.cadastreRef &&
    !!input.cadastreRef.section &&
    !!input.cadastreRef.numero;
  const cadastre = await runStage("cadastre", async () => {
    const res = await callCadastre({
      lat: geocode.lat,
      lng: geocode.lng,
      ref: refComplete
        ? {
            section: input.cadastreRef!.section,
            numero: input.cadastreRef!.numero,
            prefixe: input.cadastreRef!.prefixe,
            insee: geocode.insee,
          }
        : null,
    });
    return res.data;
  });
  let parcel = cadastre.parcel;
  const parcelAlternatives = cadastre.alternatives ?? [];

  // ─── Stage 2b: parcel disambiguation (optional, blocking) ─────────
  // When cadastre returned several candidates AND the caller plumbed an
  // `onParcelChoice` handler, pause here and let the user pick. This
  // matters most for the manual-PLU-upload fallback — the right column
  // there shows an orthophoto centred on `parcel`, so the agent needs
  // the correct parcel BEFORE reaching that step (otherwise they're
  // matching the wrong shape against the plan graphique).
  //
  // We never pause when `cadastreRef` was provided (lookup-by-ref is
  // single-result by construction) or when the picker callback isn't
  // wired — the legacy auto-pick stays the default.
  if (
    !refComplete &&
    parcelAlternatives.length > 0 &&
    input.onParcelChoice
  ) {
    emit(
      "cadastre",
      "running",
      `${parcelAlternatives.length + 1} parcelles candidates — sélection en cours…`,
    );
    parcel = await input.onParcelChoice({
      candidates: [parcel, ...parcelAlternatives],
      autoPick: parcel,
    });
  }

  const altSuffix = refComplete
    ? " (ref. document)"
    : parcelAlternatives.length > 0
      ? input.onParcelChoice
        ? " (choix utilisateur)"
        : ` · ${parcelAlternatives.length} alternative${parcelAlternatives.length > 1 ? "s" : ""}`
      : "";
  emit(
    "cadastre",
    "done",
    `Parcelle ${parcel.section}-${parcel.numero} · ${parcel.surface_m2} m²${altSuffix}`,
  );
  partial.parcel = parcel;
  partial.parcelAlternatives = parcelAlternatives;
  emitPartial();

  // ─── Stage 3: empreinte bâtie (IGN BD TOPO) ───────────────────────
  // Non-fatal : si le WFS échoue on continue sans pré-remplissage automatique.
  emit("footprint", "running");
  let footprint: FootprintSummary | null = null;
  try {
    const res = await callFootprint({
      parcelGeom: parcel.geometry,
      parcelArea_m2: parcel.surface_m2,
    });
    const data = res.data;
    footprint = {
      parcelArea_m2: data.parcelArea_m2,
      builtFootprint_m2: data.builtFootprint_m2,
      buildingCount: data.buildingCount,
      maxHeight_m: data.maxHeight_m,
    };
    const heightSuffix =
      footprint.maxHeight_m != null ? ` · ${footprint.maxHeight_m} m` : "";
    // Flag 0-building results as needing attention — legitimate for a
    // terrain nu, but in practice often signals a BD TOPO miss (recent
    // construction not yet ingested, or geometry alignment issue). The
    // pipeline still completes; the UI surfaces the ambiguity.
    const noBuildings = footprint.buildingCount === 0;
    emit(
      "footprint",
      "done",
      noBuildings
        ? "Aucun bâtiment détecté — terrain nu ou bâti récent (à vérifier)"
        : `${footprint.buildingCount} bâtiment${footprint.buildingCount > 1 ? "s" : ""} · ${footprint.builtFootprint_m2} m²${heightSuffix}`,
      { attention: noBuildings },
    );
    partial.footprint = footprint;
    emitPartial();
  } catch (e) {
    const message =
      e instanceof Error ? e.message : "BD TOPO indisponible.";
    emit("footprint", "error", `Empreinte non mesurée — ${message}`);
  }

  // ─── Stage 4: zone PLU ────────────────────────────────────────────
  emit("zone", "running");
  const zone = await runStage("zone", async () => {
    const res = await callZone({
      lat: geocode.lat,
      lng: geocode.lng,
      insee: geocode.insee,
    });
    return res.data;
  });
  emit("zone", "done", `Zone ${zone.libelle} — ${zone.libelong}`);

  // ─── Stage 5: règlement (cached, may be slow on miss) ─────────────
  if (!zone.reglement_url) {
    emit("reglement", "error", "Règlement non disponible pour cette commune.");
    throw new PipelineError({
      code: "reglement-pdf-not-found",
      stage: "reglement",
      message: "No règlement URL surfaced by GPU for this DU.",
    });
  }

  emit("reglement", "running");
  const reglement = await runStage("reglement", async () => {
    const res = await callReglement({
      insee: geocode.insee,
      zoneCode: zone.libelle,
      communeName: geocode.city,
      reglementUrl: zone.reglement_url!,
      // Pass the GPU document bundle hints so the function can locate the
      // règlement PDF inside the DU's ZIP (the /document/ SPA has no PDF links).
      partition: zone.partition || null,
      nomfic: zone.nomfic,
    });
    return res.data;
  });
  emit("reglement", "done", `${reglement.raw_sections.length} sections extraites.`);

  return { geocode, parcel, parcelAlternatives, footprint, zone, reglement };
}

// ─── Internal helpers ────────────────────────────────────────────────────
async function runStage<T>(stage: StageKey, fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (e) {
    // firebase-functions callable errors surface as {code, message, details}
    const err = e as {
      code?: string;
      message?: string;
      details?: { code?: PipelineErrorCode };
    };
    const code =
      (err.details?.code as PipelineErrorCode) ??
      (mapFirebaseCode(err.code) as PipelineErrorCode | undefined) ??
      "unknown";
    throw new PipelineError({
      code,
      stage,
      message: err.message ?? `Stage ${stage} failed.`,
      details: err.details,
    });
  }
}

// Maps Firebase HttpsError codes to our PipelineErrorCode where the mapping is
// unambiguous. Falls through to `unknown` otherwise.
function mapFirebaseCode(fbCode?: string): PipelineErrorCode | "unknown" {
  switch (fbCode) {
    case "functions/not-found":
    case "not-found":
      return "address-not-found"; // will be overridden by details.code on stages past geocode
    case "functions/deadline-exceeded":
    case "deadline-exceeded":
      return "upstream-timeout";
    case "functions/unavailable":
    case "unavailable":
      return "upstream-error";
    default:
      return "unknown";
  }
}
